import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import ChargeModal from './ChargeModal';
import RentDueModal from './RentDueModal';
import { AuthContext } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { getCurrencySymbol } from '../utils/currencyUtils';

const ChargesTab = ({ tenancyLedger, selectedListing, onDataUpdate }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showRentDueModal, setShowRentDueModal] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);

  // State for charges data with pagination
  const [charges, setCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chargeError, setChargeError] = useState(null);
  const [hasMoreCharges, setHasMoreCharges] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);

  // State for rent dues data with pagination - RESTORED
  const [rentDues, setRentDues] = useState([]);
  const [loadingRentDues, setLoadingRentDues] = useState(false);
  const [loadingMoreRentDues, setLoadingMoreRentDues] = useState(false);
  const [rentDuesError, setRentDuesError] = useState(null);
  const [hasMoreRentDues, setHasMoreRentDues] = useState(true);
  const [nextRentDuesCursor, setNextRentDuesCursor] = useState(null);
  const [editingRentDue, setEditingRentDue] = useState(null);

  // Ref for scroll detection
  const tableContainerRef = useRef(null);
  const rentDuesTableRef = useRef(null); // RESTORED

  // Reset pagination state when tenancy ledger changes
  useEffect(() => {
    console.log('ChargesTab useEffect triggered:', {
      tenancyLedger: tenancyLedger,
      tenancyLedgerID_uppercase: tenancyLedger?.ID,
      tenancyLedgerID_lowercase: tenancyLedger?.id,
      tenancyID: tenancyLedger?.tenancy_id,
      TenancyID: tenancyLedger?.TenancyID,
      allKeys: tenancyLedger ? Object.keys(tenancyLedger) : [],
      hasLedger: !!(tenancyLedger?.ID || tenancyLedger?.id),
      hasTenancyId: !!(tenancyLedger?.tenancy_id || tenancyLedger?.TenancyID)
    });

    const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;

    if (ledgerId) {
      console.log('Calling fetchCharges and fetchRentDues for tenancy ledger:', ledgerId);
      // Reset charges
      setCharges([]);
      setNextCursor(null);
      setHasMoreCharges(true);
      fetchCharges(ledgerId, null, true);

      // Reset rent dues - RESTORED
      setRentDues([]);
      setNextRentDuesCursor(null);
      setHasMoreRentDues(true);
      fetchRentDues(ledgerId, null, true);
    } else {
      console.log('No tenancy ledger ID, clearing charges and rent dues');
      setCharges([]);
      setRentDues([]);
    }
  }, [tenancyLedger?.ID, tenancyLedger?.id]);

  const fetchCharges = useCallback(async (tenancyLedgerId, cursor = null, reset = false) => {
    console.log('fetchCharges called:', { tenancyLedgerId, cursor, reset });

    if (reset) {
      setLoadingCharges(true);
    } else {
      setLoadingMore(true);
    }
    setChargeError(null);

    try {
      const queryParams = new URLSearchParams({
        pagination_type: 'cursor',
        limit: '20',
        sort_by: 'incurred_at',
        sort_dir: 'desc',
        forward: 'true'
      });

      // Add cursor if provided (for loading more)
      if (cursor) {
        queryParams.set('cursor', cursor);
      }

      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/tenancy-ledgers/${tenancyLedgerId}/charges/search?${queryParams.toString()}`;

      console.log('Making API call to:', url);
      console.log('With headers:', {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token.substring(0, 10)}...` : 'No token'
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      console.log('API response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch charges: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API response data:', data);
      const newCharges = data.data || [];

      // Normalize charge fields for UI — spread raw first so our named fields win
      const normalizedCharges = newCharges.map(charge => ({
        ...charge,
        id: charge.id || charge.ID,
        amount: charge.amount || charge.Amount,
        category_id: charge.category_id || charge.CategoryID,
        category_name: charge.category?.display_name || charge.category_name || charge.Category || '',
        note: charge.note || charge.Note || charge.description,
        incurred_at: charge.incurred_at || charge.InCurredAt || charge.charge_date || charge.date || charge.created_at,
      }));

      // Update charges list
      if (reset) {
        setCharges(normalizedCharges);
      } else {
        setCharges(prevCharges => [...prevCharges, ...normalizedCharges]);
      }

      // Update pagination state
      setNextCursor(data.next_cursor || null);
      setHasMoreCharges(data.has_more || false);

    } catch (error) {
      console.error('Error fetching charges:', error);
      setChargeError(error.message);
      if (reset) {
        setCharges([]);
      }
    } finally {
      if (reset) {
        setLoadingCharges(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, []);

  // New function to fetch rent dues - RESTORED
  const fetchRentDues = useCallback(async (tenancyLedgerId, cursor = null, reset = false) => {
    console.log('fetchRentDues called:', { tenancyLedgerId, cursor, reset });

    if (reset) {
      setLoadingRentDues(true);
    } else {
      setLoadingMoreRentDues(true);
    }
    setRentDuesError(null);

    try {
      const queryParams = new URLSearchParams({
        pagination_type: 'cursor',
        limit: '20',
        sort_by: 'due_date',
        sort_dir: 'desc',
        forward: 'true'
      });

      // Add cursor if provided (for loading more)
      if (cursor) {
        queryParams.set('cursor', cursor);
      }

      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/tenancy-ledgers/${tenancyLedgerId}/rent-dues/search?${queryParams.toString()}`;

      console.log('Making API call to:', url);
      console.log('With headers:', {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token.substring(0, 10)}...` : 'No token'
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      console.log('API response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch rent dues: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API response data:', data);
      const newRentDues = data.data || [];

      // Normalize rent due fields for UI — spread raw first so our named fields win
      const normalizedRentDues = newRentDues.map(due => ({
        ...due,
        id: due.id || due.ID,
        amount: due.amount || due.Amount,
        status: due.status || due.Status,
        note: due.note || due.Note || due.description,
        due_date: due.due_date || due.DueDate || due.date || due.created_at,
      }));

      // Update rent dues list
      if (reset) {
        setRentDues(normalizedRentDues);
      } else {
        setRentDues(prevRentDues => [...prevRentDues, ...normalizedRentDues]);
      }

      // Update pagination state
      setNextRentDuesCursor(data.next_cursor || null);
      setHasMoreRentDues(data.has_more || false);

    } catch (error) {
      console.error('Error fetching rent dues:', error);
      setRentDuesError(error.message);
      if (reset) {
        setRentDues([]);
      }
    } finally {
      if (reset) {
        setLoadingRentDues(false);
      } else {
        setLoadingMoreRentDues(false);
      }
    }
  }, []);

  // Load more charges when scrolling near bottom
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || loadingMore || !hasMoreCharges || !nextCursor) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const threshold = scrollHeight - 100; // Load when 100px from bottom

    if (scrollPosition >= threshold) {
      const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
      if (ledgerId) {
        fetchCharges(ledgerId, nextCursor, false);
      }
    }
  }, [loadingMore, hasMoreCharges, nextCursor, tenancyLedger?.ID, tenancyLedger?.id, fetchCharges]);

  // Load more rent dues when scrolling near bottom - RESTORED
  const handleRentDuesScroll = useCallback(() => {
    if (!rentDuesTableRef.current || loadingMoreRentDues || !hasMoreRentDues || !nextRentDuesCursor) return;

    const { scrollTop, scrollHeight, clientHeight } = rentDuesTableRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const threshold = scrollHeight - 100; // Load when 100px from bottom

    if (scrollPosition >= threshold) {
      const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
      if (ledgerId) {
        fetchRentDues(ledgerId, nextRentDuesCursor, false);
      }
    }
  }, [loadingMoreRentDues, hasMoreRentDues, nextRentDuesCursor, tenancyLedger?.ID, tenancyLedger?.id, fetchRentDues]);

  // Add scroll event listener for charges
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Add scroll event listener for rent dues - RESTORED
  useEffect(() => {
    const container = rentDuesTableRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleRentDuesScroll);
    return () => container.removeEventListener('scroll', handleRentDuesScroll);
  }, [handleRentDuesScroll]);


  const handleCreateCharge = () => {
    setEditingCharge(null);
    setShowChargeModal(true);
  };

  const handleEditCharge = (charge) => {
    setEditingCharge(charge);
    setShowChargeModal(true);
  };

  const handleSaveCharge = async (chargeData) => {
    console.log('Charge created successfully:', chargeData);
    setShowChargeModal(false);
    setEditingCharge(null);

    // Refresh both the tenancy ledger data and charges list
    if (onDataUpdate) {
      await onDataUpdate();
    }

    // Reset and reload charges list
    const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
    if (ledgerId) {
      setCharges([]);
      setNextCursor(null);
      setHasMoreCharges(true);
      await fetchCharges(ledgerId, null, true);
    }
  };

  const handleSaveRentDue = async (data) => {
    console.log('Rent due saved successfully:', data);
    setShowRentDueModal(false);
    setEditingRentDue(null);

    // Refresh both the tenancy ledger data and rent dues list
    if (onDataUpdate) {
      await onDataUpdate();
    }

    // Reset and reload rent dues list
    const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
    if (ledgerId) {
      setRentDues([]);
      setNextRentDuesCursor(null);
      setHasMoreRentDues(true);
      await fetchRentDues(ledgerId, null, true);
    }
  };

  const handleCreateRentDue = () => {
    setEditingRentDue(null);
    setShowRentDueModal(true);
  };

  const handleEditRentDue = (rentDue) => {
    setEditingRentDue(rentDue);
    setShowRentDueModal(true);
  };

  const handleDeleteCharge = async (charge) => {
    const chargeId = charge.id || charge.ID;
    if (!window.confirm('Are you sure you want to delete this charge? This action cannot be undone.')) return;

    const token = sessionStorage.getItem('token') ||
                 sessionStorage.getItem('access_token') ||
                 localStorage.getItem('token') ||
                 localStorage.getItem('access_token');

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

    try {
      const response = await fetch(`${baseUrl}/tenancy-ledgers/charges/${chargeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete charge: ${response.statusText}`);
      }

      if (onDataUpdate) await onDataUpdate();

      const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
      if (ledgerId) {
        setCharges([]);
        setNextCursor(null);
        setHasMoreCharges(true);
        await fetchCharges(ledgerId, null, true);
      }
    } catch (error) {
      console.error('Error deleting charge:', error);
      alert(`Error deleting charge: ${error.message}`);
    }
  };

  const handleDeleteRentDue = async (rentDue) => {
    const rentDueId = rentDue.id || rentDue.ID;
    if (!window.confirm('Are you sure you want to delete this rent due? This action cannot be undone.')) return;

    const token = sessionStorage.getItem('token') ||
                 sessionStorage.getItem('access_token') ||
                 localStorage.getItem('token') ||
                 localStorage.getItem('access_token');

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

    try {
      const response = await fetch(`${baseUrl}/tenancy-ledgers/rent-dues/${rentDueId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete rent due: ${response.statusText}`);
      }

      if (onDataUpdate) await onDataUpdate();

      const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
      if (ledgerId) {
        setRentDues([]);
        setNextRentDuesCursor(null);
        setHasMoreRentDues(true);
        await fetchRentDues(ledgerId, null, true);
      }
    } catch (error) {
      console.error('Error deleting rent due:', error);
      alert(`Error deleting rent due: ${error.message}`);
    }
  };


  // Helper function for consistent date formatting throughout this component
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  return (
    <>
      <div className="card border-0">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-receipt me-2"></i>
            Charges & Dues
          </h5>
          <button
            className="btn btn-success btn-sm"
            onClick={handleCreateCharge}
            disabled={!(tenancyLedger?.ID || tenancyLedger?.id)}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add Charge
          </button>
        </div>
        <div className="card-body">

          {/* Charges Section */}
          <div>
            <h6 className="border-bottom pb-2">
              <i className="bi bi-receipt-cutoff me-2"></i>
              Charges List ({charges.length})
            </h6>

            {/* Loading State - Initial Load */}
            {loadingCharges && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading charges...</span>
                </div>
                <p className="text-muted mt-2">Loading charges...</p>
              </div>
            )}

            {/* Error State */}
            {chargeError && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Error loading charges: {chargeError}
                <button
                  className="btn btn-outline-danger btn-sm ms-2"
                  onClick={() => {
                    const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
                    if (ledgerId) fetchCharges(ledgerId, null, true);
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Charges Content */}
            {!loadingCharges && !chargeError && (
              <>
                {charges.length > 0 ? (
                  <div
                    ref={tableContainerRef}
                    className="table-responsive"
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                  >
                    <table className="table table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Date</th>
                          <th>Category</th>
                          <th>Amount</th>
                          <th>Note</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {charges.map((charge, index) => (
                          <tr key={charge.id || charge.ID || index}>
                            <td>{(charge.incurred_at || charge.IncurredAt) ? formatDateWithContext(charge.incurred_at || charge.IncurredAt) : '-'}</td>
                            <td>{charge.category_name || charge.category?.display_name || '-'}</td>
                            <td className="fw-semibold text-dark">
                              {charge.amount?.formatted || charge.Amount?.formatted || `${currencySymbol}${((charge.amount?.amount || charge.Amount?.amount || 0) / 100).toFixed(2)}`}
                            </td>
                            <td>{charge.Note || charge.note || '-'}</td>
                            <td>
                              <div className="d-flex gap-2 justify-content-end">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEditCharge(charge)}
                                >
                                  <i className="bi bi-pencil me-1"></i>
                                  Edit
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteCharge(charge)}
                                >
                                  <i className="bi bi-trash me-1"></i>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Loading More Indicator */}
                    {loadingMore && (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading more...</span>
                        </div>
                        <small className="text-muted ms-2">Loading more charges...</small>
                      </div>
                    )}

                    {/* Load More Button */}
                    {!loadingMore && hasMoreCharges && nextCursor && (
                      <div className="text-center py-3">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => {
                            const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
                            if (ledgerId) fetchCharges(ledgerId, nextCursor, false);
                          }}
                        >
                          <i className="bi bi-arrow-down-circle me-1"></i>
                          Load More Charges
                        </button>
                      </div>
                    )}

                    {/* End of List Indicator */}
                    {!hasMoreCharges && charges.length > 0 && (
                      <div className="text-center py-3 text-muted">
                        <small>No more charges to load</small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-receipt display-1 text-muted"></i>
                    <h5 className="mt-3 text-muted">No charges recorded</h5>
                    <p className="text-muted">Click "Add Charge" to record the first charge.</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Rent Dues Section */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
              <h6 className="mb-0">
                <i className="bi bi-calendar-event me-2"></i>
                Rent Dues List ({rentDues.length})
              </h6>
              <button
                className="btn btn-success btn-sm"
                onClick={handleCreateRentDue}
                disabled={!(selectedListing?.current_tenancy?.id || selectedListing?.current_tenancy?.ID || tenancyLedger?.tenancy_id || tenancyLedger?.TenancyID)}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Rent
              </button>
            </div>

            {/* Loading State - Initial Load */}
            {loadingRentDues && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading rent dues...</span>
                </div>
                <p className="text-muted mt-2">Loading rent dues...</p>
              </div>
            )}

            {/* Error State */}
            {rentDuesError && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Error loading rent dues: {rentDuesError}
                <button
                  className="btn btn-outline-danger btn-sm ms-2"
                  onClick={() => {
                    const ledgerId = tenancyLedger?.ID || tenancyLedger?.id;
                    if (ledgerId) fetchRentDues(ledgerId, null, true);
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Rent Dues Content */}
            {!loadingRentDues && !rentDuesError && (
              <>
                {rentDues.length > 0 ? (
                  <div
                    ref={rentDuesTableRef}
                    className="table-responsive"
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                  >
                    <table className="table table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Date Range</th>
                          <th>Amount</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rentDues.map((due, index) => {
                          const periodStart = due.PeriodStart || due.period_start;
                          const periodEnd = due.PeriodEnd || due.period_end;
                          const dateRange = periodStart && periodEnd
                            ? `${formatDateWithContext(periodStart)} - ${formatDateWithContext(periodEnd)}`
                            : '-';

                          return (
                            <tr key={due.id || due.ID || index}>
                              <td>{dateRange}</td>
                              <td className="fw-semibold text-dark">
                                {due.amount?.formatted || due.Amount?.formatted || `${currencySymbol}${((due.amount?.amount || due.Amount?.amount || 0) / 100).toFixed(2)}`}
                              </td>
                              <td>
                                <div className="d-flex gap-2 justify-content-end">
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleEditRentDue(due)}
                                  >
                                    <i className="bi bi-pencil me-1"></i>
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteRentDue(due)}
                                  >
                                    <i className="bi bi-trash me-1"></i>
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Loading More Indicator */}
                    {loadingMoreRentDues && (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading more...</span>
                        </div>
                        <small className="text-muted ms-2">Loading more rent dues...</small>
                      </div>
                    )}

                    {/* No More Data Indicator */}
                    {!hasMoreRentDues && rentDues.length > 0 && (
                      <div className="text-center py-3 text-muted">
                        <small>No more rent dues to load</small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-event display-1 text-muted"></i>
                    <h5 className="mt-3 text-muted">Add initial rent due</h5>
                    <p className="text-muted">Rent dues will automatically be added on the due date after the first rent due has been manually added.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Charge Modal */}
      <ChargeModal
        show={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        charge={editingCharge}
        tenancyLedgerId={tenancyLedger?.ID || tenancyLedger?.id}
        onSave={handleSaveCharge}
      />

      {/* Rent Due Modal */}
      <RentDueModal
        show={showRentDueModal}
        onClose={() => {
          setShowRentDueModal(false);
          setEditingRentDue(null);
        }}
        rentDue={editingRentDue}
        tenancyId={selectedListing?.current_tenancy?.id || selectedListing?.current_tenancy?.ID || tenancyLedger?.tenancy_id || tenancyLedger?.TenancyID}
        onSave={handleSaveRentDue}
      />
    </>
  );
};

export default ChargesTab;
